import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Heart, ArrowLeft, Save, ChevronRight, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { copy } from '@/copy';

const CheckpointPage = () => {
  const { checkpointId } = useParams();
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  
  const [checkpoint, setCheckpoint] = useState(null);
  const [existingReflection, setExistingReflection] = useState(null);
  const [seriousAnswer, setSeriousAnswer] = useState('');
  const [lighterAnswer, setLighterAnswer] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingContinue, setPendingContinue] = useState(false);
  const [journeyComplete, setJourneyComplete] = useState(false);

  useEffect(() => {
    // Reset all state when checkpoint changes
    setCheckpoint(null);
    setExistingReflection(null);
    setSeriousAnswer('');
    setLighterAnswer('');
    setShowError(false);
    setLoading(true);
    setShowIntro(checkpointId === 'foundation');
    
    const fetchCheckpoint = async () => {
      try {
        const api = axiosAuth();
        
        // Fetch checkpoint details
        const checkpointRes = await api.get(`/checkpoints/${checkpointId}`);
        setCheckpoint(checkpointRes.data);

        // Check if journey is fully complete (all 6 done)
        const progressRes = await api.get('/progress');
        setJourneyComplete(progressRes.data.is_complete);
        
        // Try to fetch existing reflection
        try {
          const reflectionRes = await api.get(`/reflections/${checkpointId}`);
          setExistingReflection(reflectionRes.data);
          setSeriousAnswer(reflectionRes.data.serious_answer || '');
          setLighterAnswer(reflectionRes.data.lighter_answer || '');
        } catch {
          // No existing reflection
        }
      } catch (error) {
        console.error('Error fetching checkpoint:', error);
        if (error.response?.status === 403) {
          toast.error(copy.checkpoint.lockedError);
          navigate('/dashboard');
        } else {
          toast.error(copy.checkpoint.loadError);
          navigate('/dashboard');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCheckpoint();
  }, [checkpointId, axiosAuth, navigate]);

  const requestSave = (continueToNext) => {
    if (!seriousAnswer.trim()) {
      setShowError(true);
      toast.error(copy.checkpoint.requiredError);
      return;
    }
    setPendingContinue(continueToNext);
    setShowConfirm(true);
  };

  const handleSave = async (continueToNext = false) => {
    setShowConfirm(false);

    setSaving(true);
    setShowError(false);

    try {
      const api = axiosAuth();
      await api.post('/reflections', {
        checkpoint_id: checkpointId,
        serious_answer: seriousAnswer.trim(),
        lighter_answer: lighterAnswer.trim() || null
      });

      toast.success(copy.checkpoint.savedToast);

      if (continueToNext) {
        // Fetch updated checkpoints to find next one
        const checkpointsRes = await api.get('/checkpoints');
        const checkpoints = checkpointsRes.data;
        const currentIndex = checkpoints.findIndex(cp => cp.id === checkpointId);
        
        if (currentIndex < checkpoints.length - 1) {
          const nextCheckpoint = checkpoints[currentIndex + 1];
          if (!nextCheckpoint.is_locked) {
            navigate(`/checkpoint/${nextCheckpoint.id}`);
          } else {
            navigate('/dashboard');
          }
        } else {
          // All checkpoints complete
          navigate('/summary');
        }
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error saving reflection:', error);
      toast.error(copy.checkpoint.saveError);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#1D2D44] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#1D2D44] font-medium">{copy.checkpoint.loadingCheckpoint}</p>
        </div>
      </div>
    );
  }

  if (!checkpoint) {
    return null;
  }

  if (showIntro) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex flex-col">
        <header className="bg-white shadow-sm">
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-center">
            <span className="font-serif text-lg text-[#1D2D44]">
              Our Love <Heart className="inline-block w-4 h-4 text-[#C9A84C] mx-1" strokeWidth={1.5} /> Connection
            </span>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <Card className="w-full max-w-lg border-none shadow-lg rounded-2xl overflow-hidden" data-testid="map-disclaimer-card">
            <div className="h-1 bg-[#B89B5E]"></div>
            <CardContent className="p-8 sm:p-10">
              <h2 className="font-serif text-2xl text-[#1D2D44] mb-6" data-testid="map-disclaimer-title">{copy.mapDisclaimer.noteTitle}</h2>
              <div className="space-y-4 text-[#3A4A5A] text-sm leading-relaxed">
                <p>{copy.mapDisclaimer.line1}</p>
                <p>{copy.mapDisclaimer.line2}</p>
                <p>{copy.mapDisclaimer.line3}</p>
              </div>
              <Button
                onClick={() => setShowIntro(false)}
                className="w-full mt-8 bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full h-12 text-base font-medium shadow-lg transition-all duration-300 hover:scale-[1.01]"
                data-testid="map-disclaimer-begin-btn"
              >
                {copy.mapDisclaimer.cta}
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/dashboard" 
            className="flex items-center text-[#424E59] hover:text-[#1D2D44] transition-colors"
            data-testid="back-to-dashboard-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">{copy.nav.backToJourney}</span>
          </Link>
          
          <span className="font-serif text-lg text-[#1D2D44]">
            Our Love <Heart className="inline-block w-4 h-4 text-[#C9A84C] mx-1" strokeWidth={1.5} /> Connection
          </span>
          
          <div className="w-20"></div>
        </div>
      </header>

      {/* Progress indicator */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between text-sm text-[#3A4A5A]">
            <span>{copy.checkpoint.checkpointOf(checkpoint.order, 6)}</span>
            <span className="text-[#5E7F63]">{checkpoint.name}</span>
          </div>
          <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#5E7F63] transition-all duration-500"
              style={{ width: `${(checkpoint.order / 6) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Checkpoint Header */}
        <div className="text-center mb-10 animate-fade-in">
          <p className="text-[#5E7F63] uppercase tracking-widest text-sm mb-2">
            {checkpoint.subtitle}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#1D2D44]">
            {checkpoint.name}
          </h1>
        </div>

        {/* Serious Question */}
        <Card className="mb-8 border-none shadow-lg rounded-2xl overflow-hidden animate-slide-up">
          <div className="h-1 bg-[#B89B5E]"></div>
          <CardContent className="p-6 sm:p-8">
            <div className="mb-6">
              <Label className="text-[#1D2D44] font-serif text-lg leading-relaxed">
                {checkpoint.serious_question}
              </Label>
              {!journeyComplete && (
                <p className="text-[#5E7F63] text-sm mt-2 flex items-center">
                  <span className="w-1.5 h-1.5 bg-[#5E7F63] rounded-full mr-2"></span>
                  {copy.checkpoint.requiredHelper}
                </p>
              )}
            </div>
            
            {journeyComplete ? (
              <div className="bg-[#F5F5F5] rounded-xl p-4 text-[#1D2D44] text-base leading-relaxed whitespace-pre-wrap" data-testid="serious-answer-readonly">
                {seriousAnswer}
              </div>
            ) : (
              <Textarea
                value={seriousAnswer}
                onChange={(e) => {
                  setSeriousAnswer(e.target.value);
                  if (showError && e.target.value.trim()) setShowError(false);
                }}
                placeholder={copy.checkpoint.textareaPlaceholder}
                className={`min-h-[200px] bg-[#F5F5F5] border-transparent focus:border-[#B89B5E] focus:ring-[#B89B5E]/20 rounded-xl text-base leading-relaxed resize-none ${
                  showError ? 'border-red-400 bg-red-50/50' : ''
                }`}
                data-testid="serious-answer-textarea"
              />
            )}
            
            {showError && (
              <p className="text-red-500 text-sm mt-2 flex items-center" data-testid="required-error-message">
                <AlertCircle className="w-4 h-4 mr-1" />
                {copy.checkpoint.requiredError}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Lighter Question */}
        <Card className="mb-8 border-none shadow-md rounded-2xl overflow-hidden animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <CardContent className="p-6 sm:p-8 bg-[#A3B8A6]/10">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Label className="text-[#1D2D44] font-serif text-lg leading-relaxed">
                  {checkpoint.lighter_question}
                </Label>
              </div>
              <p className="text-[#424E59] text-sm italic">
                {copy.checkpoint.optionalLabel}
              </p>
            </div>
            
            {journeyComplete ? (
              <div className="bg-white rounded-xl p-4 text-[#1D2D44] text-base leading-relaxed whitespace-pre-wrap" data-testid="lighter-answer-readonly">
                {lighterAnswer || <span className="text-[#B5ADA7] italic">Not answered</span>}
              </div>
            ) : (
              <Textarea
                value={lighterAnswer}
                onChange={(e) => setLighterAnswer(e.target.value)}
                placeholder={copy.checkpoint.optionalPlaceholder}
                className="min-h-[120px] bg-white border-transparent focus:border-[#5E7F63] focus:ring-[#5E7F63]/20 rounded-xl text-base leading-relaxed resize-none"
                data-testid="lighter-answer-textarea"
              />
            )}
          </CardContent>
        </Card>

        {/* Action Buttons or Read-Only Notice */}
        {journeyComplete ? (
          <div className="text-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <p className="text-[#424E59] text-sm mb-4" data-testid="readonly-notice">{copy.checkpoint.readOnlyNotice}</p>
            <Link to="/dashboard">
              <Button className="bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full px-8 py-5" data-testid="back-to-dashboard-btn">
                {copy.nav.backToJourney}
              </Button>
            </Link>
          </div>
        ) : (
          <>
        <div className="flex flex-col sm:flex-row gap-4 justify-between animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <Button
            onClick={() => requestSave(false)}
            disabled={saving}
            variant="outline"
            className="border-[#1D2D44] text-[#1D2D44] hover:bg-[#F5F5F5] rounded-full px-6 py-5"
            data-testid="save-and-pause-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {copy.checkpoint.saveAndPause}
          </Button>
          
          <Button
            onClick={() => requestSave(true)}
            disabled={saving}
            className="bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full px-8 py-5 shadow-lg transition-all duration-300 hover:scale-[1.02]"
            data-testid="save-and-continue-btn"
          >
            {saving ? (
              <span className="flex items-center">
                <span className="w-4 h-4 border-2 border-[#B89B5E] border-t-transparent rounded-full animate-spin mr-2"></span>
                {copy.checkpoint.saving}
              </span>
            ) : (
              <>
                {checkpoint.order === 6 ? copy.checkpoint.completeJourney : copy.checkpoint.saveAndContinue}
                <ChevronRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>

        <p className="text-center text-[#424E59] text-sm mt-8">
          {copy.checkpoint.progressSaved}
        </p>
          </>
        )}

        {/* Confirmation Dialog */}
        {showConfirm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4" data-testid="confirm-overlay">
            <Card className="w-full max-w-md border-none shadow-2xl rounded-2xl overflow-hidden animate-fade-in">
              <div className="h-1 bg-[#B89B5E]"></div>
              <CardContent className="p-8">
                <p className="text-[#1D2D44] text-sm leading-relaxed mb-2">{copy.checkpoint.confirmMessage}</p>
                <p className="text-[#1D2D44] font-medium text-sm mb-6">{copy.checkpoint.confirmPrompt}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => setShowConfirm(false)}
                    variant="outline"
                    className="flex-1 border-[#1D2D44] text-[#1D2D44] hover:bg-[#F5F5F5] rounded-full"
                    data-testid="confirm-go-back-btn"
                  >
                    {copy.checkpoint.confirmGoBack}
                  </Button>
                  <Button
                    onClick={() => handleSave(pendingContinue)}
                    className="flex-1 bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full"
                    data-testid="confirm-yes-btn"
                  >
                    {copy.checkpoint.confirmYes}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default CheckpointPage;
